import type { Scale } from '@tunasong/schemas'
import ScaleLib from '../scale/scale.js'
import type { ChordFunction } from './types.js'

/**
 * Roman-numeral → screen-reader label table. Owned by Bowie. The label is the
 * functional name (e.g. "Five, dominant"), and the caller may suffix the scale
 * (e.g. "in C major") for full disambiguation. `chromatic` returns null
 * because no chip is rendered for chromatic chords.
 */
const LABELS: Record<Exclude<ChordFunction, 'chromatic'>, string> = {
  I: 'One, tonic',
  ii: 'Two minor, supertonic',
  iii: 'Three minor, mediant',
  IV: 'Four, subdominant',
  V: 'Five, dominant',
  vi: 'Six minor, submediant',
  'vii°': 'Seven diminished, leading tone',
  i: 'One minor, tonic',
  'ii°': 'Two diminished, supertonic',
  '♭III': 'Flat three, mediant',
  iv: 'Four minor, subdominant',
  v: 'Five minor, dominant',
  '♭VI': 'Flat six, submediant',
  '♭VII': 'Flat seven, subtonic',
  '♭II': 'Flat two, Phrygian',
  'i°': 'One diminished, tonic',
  'iii°': 'Three diminished, mediant',
  'v°': 'Five diminished, dominant',
  'vi°': 'Six diminished, submediant',
  '♭iii': 'Flat-three, mediant',
  '♭V': 'Flat-five, major triad on flat-five',
  '♭vii': 'Flat-seven, subtonic',
  II: 'Two, major triad on the second',
  '#iv°': 'Sharp four diminished, Lydian characteristic',
  vii: 'Seven, minor on the seventh',
  '♭III+': 'Flat three augmented, harmonic minor',
  'V/ii': 'Five of two, secondary dominant',
  'V/IV': 'Five of four, secondary dominant',
  'V/V': 'Five of five, secondary dominant',
  'V/vi': 'Five of six, secondary dominant',
  'V/iv': 'Five of four minor, secondary dominant',
  'V/♭VII': 'Five of flat seven, secondary dominant',
  'subV/I': 'Tritone sub of one chord, secondary dominant substitute',
  'subV/ii': 'Tritone sub of two chord, secondary dominant substitute',
  'subV/IV': 'Tritone sub of four chord, secondary dominant substitute',
  'subV/V': 'Tritone sub of five chord, secondary dominant substitute',
  'subV/vi': 'Tritone sub of six chord, secondary dominant substitute',
  open: 'Open chord (no third)',
}

/**
 * True when the chord function is a Roman numeral that surfaces may print as a
 * chip, superscript or parenthetical. `'chromatic'` (no relationship to the
 * key) and `'open'` (in the key, but no third, so no degree quality) are
 * labels, not numerals.
 */
export const isNumeralChordFunction = (chordFunction: ChordFunction): boolean =>
  chordFunction !== 'chromatic' && chordFunction !== 'open'

export const getChordFunctionAriaLabel = (chordFunction: ChordFunction, scale: Scale | null): string | null => {
  if (chordFunction === 'chromatic') {
    return null
  }
  const base = LABELS[chordFunction]
  return scale ? `${base} in ${ScaleLib.getName(scale)}` : base
}
